<template>
  <div>
    <RevBreadcrumb :ariaLabel="i18n(translations.breadcrumb)" :breads />
    <RevContainer>
      <ActionBanner
        v-if="orderDetails?.instruction"
        :actions="infoBannerActions"
        class="mb-32"
        :instruction="orderDetails.instruction"
        @action="handleAction"
      />
      <RevCard
        v-if="orderDetails?.activeStep && orderDetails?.timeline.length"
        class="mb-32 px-20 pb-8 pt-32 md:px-72 md:py-32 lg:px-[188px]"
      >
        <ClientOnly>
          <template #placeholder>
            <StatusTrackerSkeleton :steps="orderDetails.timeline" />
          </template>
          <template #default>
            <RevStatusTracker
              :key="orderDetails?.timeline.length"
              :current-step="orderDetails?.activeStep"
              :orientation="statusTrackerOrientation"
              progress-bar-color="accent"
              :steps="orderDetails?.timeline"
            >
              <template
                v-for="step in orderDetails.timeline"
                #[`${step.key}-date`]
              >
                <TheTime
                  v-if="step.date"
                  :key="step.key"
                  :datetime="step.date"
                  :options="{
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                  }"
                />
              </template>

              <template
                v-for="step in orderDetails?.timeline"
                #[`${step.key}-details`]
              >
                <p v-if="step" :key="step.key">
                  {{ step.details }}
                </p>
              </template>
            </RevStatusTracker>
          </template>
        </ClientOnly>
      </RevCard>
      <div class="flex flex-col gap-32 md:flex-row">
        <CustomerAssessment
          v-if="orderDetails?.product.customerAssessment"
          :assessment="orderDetails.product.customerAssessment"
          :counter-offer-price="orderDetails.product.counterOfferPrice"
          :image="orderDetails.product.image"
          :is-loading-counter-offer="isLoadingCounterOfferData"
          :order-id="orderDetails.id"
          :price="orderDetails.product.price"
          :tag="orderDetails.product.tag"
          :title="orderDetails.product.title"
          @view-counteroffer="handleOpenCounterOfferModal"
        />
        <CustomerInformation
          v-if="orderDetails?.customer?.address"
          :address="orderDetails.customer.address"
          :identity-documents-files="
            orderDetails.customer.identityDocumentsFiles
          "
          :order-id="orderDetails.id"
          :phone-number="orderDetails.customer.phoneNumber"
          :tracking-number="orderDetails.customer.trackingNumber"
          :transfer-certificate="orderDetails.customer.transferCertificate"
        />
      </div>
    </RevContainer>

    <SendPaymentModal
      v-if="orderDetails?.id"
      :order-id="orderDetails.id"
      @confirmed="handlePaymentConfirmed"
      @error="handlePaymentError"
    />
    <OrderOnHoldModal
      :actions="onHoldActions"
      :offer-price="orderDetails.product.price"
      :order-id="orderDetails.id"
      @error="handleOnHoldError"
      @success="handleOnHoldSuccess"
    />
    <CounterOfferSummaryModal
      :counter-offer="counterOfferDetails"
      :is-staff="false"
    />
  </div>
</template>

<script setup lang="ts">
import { navigateTo, useRoute } from '#imports'
import { computed, ref } from 'vue'

import type { HttpApiError } from '@backmarket/http-api'
import {
  getRefurbisherCounterOfferDetails,
  getRefurbisherOrder,
  getRefurbisherOrderOnHoldActions,
} from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher'
import type {
  BuybackOrderDetails,
  BuybackOrderDetailsOnHoldAction,
  RefurbisherCounterOfferDetails,
} from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher.types'
import CounterOfferSummaryModal from '@backmarket/nuxt-layer-buyback/components/CounterOfferSummaryModal/CounterOfferSummaryModal.vue'
import StatusTrackerSkeleton from '@backmarket/nuxt-layer-buyback/components/StatusTrackerSkeleton/StatusTrackerSkeleton.vue'
import { REFURBISHER_ACTIONS } from '@backmarket/nuxt-layer-buyback/constants'
import MODAL_NAMES from '@backmarket/nuxt-layer-buyback/utils/modals'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { Breakpoint } from '@backmarket/utils/dom/getCurrentBreakpoint'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevBreadcrumb } from '@ds/components/Breadcrumb'
import { RevCard } from '@ds/components/Card'
import { RevContainer } from '@ds/components/Container'
import { openModal } from '@ds/components/ModalBase'
import { RevStatusTracker } from '@ds/components/StatusTracker'

import { ROUTE_NAMES } from '~/constants/routes'
import { orderDetailsAdapter } from '~/scopes/buyback/pages/orderDetails/OrderDetailsAdapter'
import ActionBanner from '~/scopes/buyback/pages/orderDetails/components/ActionBanner/ActionBanner.vue'
import CustomerAssessment from '~/scopes/buyback/pages/orderDetails/components/CustomerAssessment/CustomerAssessment.vue'
import CustomerInformation from '~/scopes/buyback/pages/orderDetails/components/CustomerInformation/CustomerInformation.vue'
import OrderOnHoldModal from '~/scopes/buyback/pages/orderDetails/components/OrderOnHoldModal/OrderOnHoldModal.vue'
import SendPaymentModal from '~/scopes/buyback/pages/orderDetails/components/SendPaymentModal/SendPaymentModal.vue'
import { SEE_COUNTER_OFFER } from '~/scopes/buyback/pages/orderDetails/constants'

import translations from './OrderDetails.translations'

const i18n = useI18n()
const logger = useLogger()
const route = useRoute()
const breakpoint = useClientOnlyComputedBreakpoint()
const actionLoadingId = ref('')
const isLoadingCounterOfferData = ref(false)
const counterOfferDetails = ref<RefurbisherCounterOfferDetails | null>(null)
const onHoldActions = ref<BuybackOrderDetailsOnHoldAction[]>([])
const { openSuccessToast, openErrorToast } = useTheToast()

const { error: getRefurbisherOrderError, data: getRefurbisherOrderData } =
  await useHttpFetch(getRefurbisherOrder, {
    pathParams: {
      id: route.params.id,
    },
  })

const shouldRedirect =
  getRefurbisherOrderError.value?.statusCode &&
  [404, 403].includes(getRefurbisherOrderError.value.statusCode)

if (shouldRedirect) {
  navigateTo({
    name: ROUTE_NAMES.BUYBACK.ORDERS.ALL,
  })
}

if (getRefurbisherOrderError.value && !shouldRedirect) {
  logger.error('[BUYBACK][API][ORDER] Error getting order', {
    error: getRefurbisherOrderError.value,
    owners: ['bot-squad-circularity-order-lifecycle-front'],
  })
}

const orderDetails = computed(() =>
  orderDetailsAdapter(getRefurbisherOrderData.value),
)

function isInfoBannerAction(action: BuybackOrderDetails['actions'][number]) {
  return action.id !== SEE_COUNTER_OFFER
}

async function getOnHoldActions() {
  if (!isEmpty(onHoldActions.value)) {
    // do not call  the API if data have been already returned
    openModal(MODAL_NAMES.REFURBISHER_ORDER_ON_HOLD)

    return
  }
  try {
    actionLoadingId.value = REFURBISHER_ACTIONS.PUT_ON_HOLD
    const payload = await $httpFetch(getRefurbisherOrderOnHoldActions, {
      pathParams: {
        id: orderDetails.value?.id,
      },
    })

    onHoldActions.value = payload
    openModal(MODAL_NAMES.REFURBISHER_ORDER_ON_HOLD)
  } catch (err) {
    openErrorToast({
      title: i18n(translations.errorMessage),
      content: i18n(translations.errorTitle),
    })

    logger.error('[BUYBACK][API][ORDER] Error getting order on hold actions', {
      error: err as HttpApiError,
      owners: ['bot-squad-circularity-order-lifecycle-front'],
    })
  }
  actionLoadingId.value = ''
}

async function handleOpenCounterOfferModal() {
  try {
    isLoadingCounterOfferData.value = true
    const payload = await $httpFetch(getRefurbisherCounterOfferDetails, {
      pathParams: {
        id: orderDetails.value?.id,
      },
    })
    counterOfferDetails.value = payload
    openModal(MODAL_NAMES.VIEW_COUNTER_OFFER)
  } catch (err) {
    openErrorToast({
      title: i18n(translations.errorMessage),
      content: i18n(translations.errorTitle),
    })

    logger.error('[BUYBACK][API][ORDER] Error getting counter offer', {
      error: err as HttpApiError,
      owners: ['bot-squad-circularity-order-lifecycle-front'],
    })
  } finally {
    isLoadingCounterOfferData.value = false
  }
}

async function handleAction(action: BuybackOrderDetails['actions'][number]) {
  switch (action.id) {
    case REFURBISHER_ACTIONS.VALIDATE:
    case REFURBISHER_ACTIONS.VALIDATE_ACTION:
      openModal(MODAL_NAMES.REFURBISHER_ORDER_SEND_PAYMENT)
      break

    case REFURBISHER_ACTIONS.PUT_ON_HOLD:
      await getOnHoldActions()
      break

    default:
  }
}

async function refreshOrderStatus() {
  // refresh order status when payment is done
  try {
    const payload = await $httpFetch(getRefurbisherOrder, {
      pathParams: {
        id: route.params.id,
      },
    })

    getRefurbisherOrderData.value = { ...payload }
  } catch (err) {
    openErrorToast()

    logger.error('[BUYBACK][API][ORDER] Error getting order', {
      error: err as HttpApiError,
      owners: ['bot-squad-circularity-order-lifecycle-front'],
    })
  }
}

function handleOnHoldError() {
  openErrorToast({
    title: i18n(translations.errorTitle),
    content: i18n(translations.errorMessage),
  })
}

function handleOnHoldSuccess({
  message,
  title,
}: {
  message: string
  title: string
}) {
  refreshOrderStatus()
  openSuccessToast({
    title,
    content: message,
  })
}

async function handlePaymentConfirmed() {
  openSuccessToast({
    title: i18n(translations.paymentSuccessTitle),
    content: i18n(translations.paymentSuccessMessage),
  })

  await refreshOrderStatus()
}

function handlePaymentError() {
  openSuccessToast({
    title: i18n(translations.errorTitle),
    content: i18n(translations.errorMessage),
  })
}

const infoBannerActions = computed(() =>
  orderDetails.value?.actions
    ?.filter(isInfoBannerAction)
    .map((action, index) => ({
      ...action,
      variant: index === 0 ? 'primary' : 'secondary',
      loading: action.id === actionLoadingId.value,
    })),
)

const statusTrackerOrientation = computed(() =>
  breakpoint.value < Breakpoint.MD ? 'vertical' : 'horizontal',
)

export type InfoBannerActions = typeof infoBannerActions.value

const breads = computed(() => {
  return [
    {
      title: i18n(translations.ordersBreadcrumbTitle),
      link: {
        name: ROUTE_NAMES.BUYBACK.ORDERS.ALL,
      },
    },
    ...(typeof route.params.id === 'string'
      ? [
          {
            title: route.params.id,
            link: {
              name: ROUTE_NAMES.BUYBACK.ORDERS.DETAILS,
              params: route.params,
            },
          },
        ]
      : []),
  ]
})
</script>
